import {
  AbsoluteCenter,
  Center,
  Container,
  HStack,
  Icon,
  IconButton,
  Pagination,
  ProgressCircle,
  SkeletonText,
  Table,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCaretDown, FaCaretUp, FaCheck } from 'react-icons/fa';
import { FaAngleDown, FaAngleUp, FaAnglesUp, FaEquals } from 'react-icons/fa6';
import { IoIosCheckmarkCircle, IoIosCloseCircle } from 'react-icons/io';
import { z } from 'zod';

import { ActionsService } from '@/client/index.ts';
import AddAction from '@/components/actions/AddAction';
import FilterActions from '@/components/actions/FilterActions';
import ActionsMenu from '@/components/common/ActionsMenu.tsx';
import Navbar from '@/components/common/Navbar.tsx';

// Validate and provide defaults for search params
const actionsSearchSchema = z.object({
  page: z.number().catch(1),
  filter: z.string().catch(''),
  sort: z.string().catch(''),
});

type ActionFilterType = {
  page: number;
  filter: string;
  sort: string;
};

export const Route = createFileRoute('/_layout/$slug/actions')({
  component: RouteComponent,
  validateSearch: (search) => actionsSearchSchema.parse(search),
});

const PER_PAGE = 10;

function getActionsQueryOptions({
  slug,
  page,
  filter,
  sort,
}: {
  slug: string;
  page: number;
  filter: string;
  sort: string;
}) {
  return {
    queryKey: ['actions', { page, filter, sort }],
    queryFn: async () => {
      return await ActionsService.readActions({
        offset: (page - 1) * PER_PAGE,
        limit: PER_PAGE,
        companySlug: slug,
        filter: filter,
        sort: sort,
      });
    },
  };
}

function ActionsTable() {
  const navigate = useNavigate({ from: Route.fullPath });
  const { t } = useTranslation();
  const { page, filter, sort } = Route.useSearch();
  const { slug } = useParams({ strict: false });

  const parsedSorting = useMemo(() => {
    return sort ? [{ id: sort.split(':')[0], desc: sort.split(':')[1] === 'desc' }] : [];
  }, [sort]);

  const { data: actionsData, isLoading } = useQuery({
    ...getActionsQueryOptions({ slug, page, filter, sort }),
  });

  const columns = [
    {
      accessorKey: 'name',
      header: t('action.columnsHeader.name'),
    },
    {
      accessorKey: 'priority',
      header: t('action.columnsHeader.priority'),
      cell: ({ row }: { row: any }) => (
        <Center>
          {row.original.priority ? (
            <Icon fontSize="2xl" color="green.500">
              <IoIosCheckmarkCircle />
            </Icon>
          ) : (
            <Icon fontSize="2xl" color="red.600">
              <IoIosCloseCircle />
            </Icon>
          )}
        </Center>
      ),
    },
    {
      accessorKey: 'archived',
      header: t('action.columnsHeader.archived'),
      cell: ({ row }: { row: any }) => (
        <Center>
          {row.original.archived ? (
            <Icon fontSize="2xl" color="green.500">
              <IoIosCheckmarkCircle />
            </Icon>
          ) : (
            <Icon fontSize="2xl" color="red.600">
              <IoIosCloseCircle />
            </Icon>
          )}
        </Center>
      ),
    },
    {
      accessorKey: 'locked',
      header: t('action.columnsHeader.locked'),
      cell: ({ row }: { row: any }) => (
        <Center>
          {row.original.locked ? (
            <Icon fontSize="2xl" color="green.500">
              <IoIosCheckmarkCircle />
            </Icon>
          ) : (
            <Icon fontSize="2xl" color="red.600">
              <IoIosCloseCircle />
            </Icon>
          )}
        </Center>
      ),
    },
    {
      accessorKey: 'start_date',
      header: t('action.columnsHeader.startDate'),
      width: 120,
    },
    {
      accessorKey: 'end_date',
      header: t('action.columnsHeader.endDate'),
      width: 120,
    },
    {
      accessorKey: 'impact',
      header: t('action.columnsHeader.impact'),
      cell: ({ row }: { row: any }) => (
        <Center>
          {row.original.impact === 'low' ? (
            <Icon fontSize="2xl" color="blue.500">
              <FaAngleDown />
            </Icon>
          ) : row.original.impact === 'medium' ? (
            <Icon fontSize="2xl" color="yellow.500">
              <FaEquals />
            </Icon>
          ) : row.original.impact === 'high' ? (
            <Icon fontSize="2xl" color="red.500">
              <FaAngleUp />
            </Icon>
          ) : row.original.impact === 'very_high' ? (
            <Icon fontSize="2xl" color="red.500">
              <FaAnglesUp />
            </Icon>
          ) : (
            'N/A'
          )}
        </Center>
      ),
    },
    {
      accessorKey: 'progression',
      header: 'Progression',
      cell: ({ row }: { row: any }) => (
        <Center>
          <ProgressCircle.Root size="md" value={row.original.progression || 0} colorPalette="primary">
            <ProgressCircle.Circle>
              <ProgressCircle.Track />
              <ProgressCircle.Range strokeLinecap="round" />
            </ProgressCircle.Circle>
            <AbsoluteCenter>
              {row.original.progression !== null && row.original.progression < 100 && <ProgressCircle.ValueText />}
              {row.original.progression === 100 && (
                <Icon color="colorPalette.solid">
                  <FaCheck />
                </Icon>
              )}
            </AbsoluteCenter>
          </ProgressCircle.Root>
        </Center>
      ),
    },
    {
      id: 'actions', // Non-data column needs an id
      header: '',
      cell: ({ row }: { row: any }) => (
        <Center>
          <ActionsMenu type={'Action'} value={row.original} />
        </Center>
      ),
      maxWidth: 50,
    },
  ];

  const table = useReactTable({
    data: actionsData?.data || [],
    columns,
    state: {
      globalFilter: filter,
      sorting: parsedSorting,
    },
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(parsedSorting) : updater;
      const newSort = newSorting.length ? `${newSorting[0].id}:${newSorting[0].desc ? 'desc' : 'asc'}` : '';
      navigate({
        search: (prev: Record<string, ActionFilterType>) => ({
          ...prev,
          sort: newSort,
        }),
      });
    },
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const onChangeFilter = (value: string) => {
    navigate({
      search: (prev: Record<string, ActionFilterType>) => ({
        ...prev,
        filter: value,
      }),
    });
  };

  useEffect(() => {
    if (actionsData && actionsData?.count < page * PER_PAGE && page > 1) {
      navigate({
        search: (prev: Record<string, ActionFilterType>) => ({
          ...prev,
          page: Math.floor(actionsData?.count / PER_PAGE) + 1,
        }),
      });
    }
  });

  return (
    <>
      <FilterActions filter={filter} changeFilter={onChangeFilter} />
      <Table.Root size={{ base: 'sm', md: 'md' }} striped>
        {table.getHeaderGroups().map((headerGroup) => (
          <Table.Header key={headerGroup.id}>
            <Table.Row>
              {headerGroup.headers.map((header) => (
                <Table.ColumnHeader key={header.id} onClick={header.column.getToggleSortingHandler()}>
                  <Center inline gap="2">
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getIsSorted() ? (
                      header.column.getIsSorted() === 'desc' ? (
                        <FaCaretDown aria-label="sorted descending" />
                      ) : (
                        <FaCaretUp aria-label="sorted ascending" />
                      )
                    ) : null}
                  </Center>
                </Table.ColumnHeader>
              ))}
            </Table.Row>
          </Table.Header>
        ))}
        {isLoading ? (
          <Table.Body>
            {new Array(PER_PAGE).fill(null).map((_, i) => (
              <Table.Row key={i}>
                {new Array(9).fill(null).map((_, j) => (
                  <Table.Cell key={j}>
                    <SkeletonText noOfLines={1} paddingBlock="16px" />
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        ) : (
          <Table.Body>
            {table.getRowModel().rows.map((row) => (
              <Table.Row key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Table.Cell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        )}
      </Table.Root>
      <Pagination.Root
        count={actionsData?.count || 0}
        pageSize={PER_PAGE}
        page={page}
        onPageChange={(pageDetails) =>
          navigate({
            search: (prev: Record<string, ActionFilterType>) => ({
              ...prev,
              page: pageDetails.page,
            }),
          })
        }
      >
        <Center>
          <HStack wrap="wrap" mt={4}>
            <Pagination.PrevTrigger />
            <Pagination.Items
              render={(page) => <IconButton variant={{ base: 'ghost', _selected: 'outline' }}>{page.value}</IconButton>}
            />
            <Pagination.NextTrigger />
          </HStack>
        </Center>
      </Pagination.Root>
    </>
  );
}

function RouteComponent() {
  return (
    <Container maxW="full">
      <Navbar type={'Action'} addModalAs={AddAction} />
      <ActionsTable />
    </Container>
  );
}
